import { db } from "./db";
import { DIRECTIONS, type Direction, type ProgressRecord, type VocabCacheMeta, type VocabEntry } from "./types";
import { fetchVocab } from "./api";
import vocabJson from "../data/vocab.json";

/**
 * Module-level in-memory cache. Initialised to the bundled JSON so tests
 * (and the very first ever offline boot) have a complete list before
 * `initVocab()` runs. `initVocab()` overwrites this with either the
 * IDB-cached blob or a fresh server fetch.
 */
let VOCAB: VocabEntry[] = vocabJson as VocabEntry[];
let initialised = false;

/**
 * Resolve the vocab list for this app session. Strategy:
 *   1. Read the cached blob from IDB (if any) and prime VOCAB optimistically
 *      so the rest of the boot can proceed against a non-empty list.
 *   2. Call `GET /vocab` with the cached ETag. If the server says 304 we
 *      keep the cached blob; if 200 we overwrite both VOCAB and the IDB
 *      cache with the fresh response.
 *   3. On network error: keep whatever was in VOCAB (cache or bundled JSON
 *      seed). Quiz still works offline; admin edits propagate on next boot.
 */
export async function initVocab(): Promise<VocabEntry[]> {
  const cached = (await db.meta.get("vocab-cache")) as VocabCacheMeta | undefined;
  if (cached) {
    VOCAB = cached.data;
  }
  // else: keep the bundled-JSON seed already in VOCAB

  try {
    const result = await fetchVocab(cached?.etag);
    if (result.kind === "fresh") {
      VOCAB = result.vocab;
      const record: VocabCacheMeta = {
        key: "vocab-cache",
        etag: result.etag,
        data: result.vocab,
        cachedAt: Date.now(),
      };
      await db.meta.put(record);
    }
    // not_modified: keep the cached blob already in VOCAB
  } catch {
    // Offline or server hiccup. VOCAB already holds the best-available
    // snapshot (cache or bundled seed). Boot continues.
  }

  initialised = true;
  return VOCAB;
}

export function freshProgress(entry: VocabEntry, direction: Direction): ProgressRecord {
  return {
    english: entry.english,
    direction,
    remainingCorrect: 2,
    state: "fresh",
    totalAsked: 0,
    totalCorrect: 0,
    totalWrong: 0,
    lastSeenAt: 0,
    lastAnsweredCorrect: null,
    consecutiveCorrect: 0,
    earliestQuestionIndex: 0,
  };
}

/**
 * Idempotent local-progress migration + seeding against the *active* vocab
 * list. Phase 2 von Issue #95: seedet pro Wort zwei Rows (en_de + de_en),
 * damit beide Richtungen sofort verfügbar sind. Orphan-Sweep entfernt Rows
 * deren english-Key nicht mehr im Pool ist (admin soft-deleted).
 *
 * Pre-condition: `initVocab()` ran. Defensive: if VOCAB is empty we still
 * do the orphan sweep but skip the bulkAdd.
 */
export async function ensureSeeded(): Promise<boolean> {
  const vocabKeys = new Set(VOCAB.map((v) => v.english));
  // Compound-PK aus Dexie: jeder Eintrag ist [english, direction].
  const existingPks = (await db.progress.toCollection().primaryKeys()) as Array<[string, Direction]>;
  const existingSet = new Set(existingPks.map(([e, d]) => `${e}|${d}`));

  // Orphan = english-Key nicht mehr in vocab. Lösche alle Direction-Rows zu dem Key.
  const orphanPks = existingPks.filter(([e]) => !vocabKeys.has(e));

  // Missing = pro Vokabel pro Direction. Falls die Direction-Row noch nicht existiert.
  const missing: ProgressRecord[] = [];
  for (const entry of VOCAB) {
    for (const dir of DIRECTIONS) {
      if (!existingSet.has(`${entry.english}|${dir}`)) {
        missing.push(freshProgress(entry, dir));
      }
    }
  }

  const sessionExists = !!(await db.meta.get("session"));
  const nothingToDo = orphanPks.length === 0 && missing.length === 0 && sessionExists;
  if (nothingToDo) return false;

  await db.transaction("rw", db.progress, db.meta, async () => {
    if (orphanPks.length > 0) {
      await db.progress.bulkDelete(orphanPks);
    }
    if (missing.length > 0) {
      await db.progress.bulkAdd(missing);
    }
    if (!sessionExists) {
      await db.meta.put({
        key: "session",
        questionsAnswered: 0,
        startedAt: Date.now(),
        totalElapsedMs: 0,
      });
    }
  });
  return true;
}

export async function countProgress(): Promise<number> {
  return db.progress.count();
}

export function getVocab(): VocabEntry[] {
  return VOCAB;
}

/** Test-only escape hatch — resets the module-level cache so tests can
 *  exercise the init flow against a clean slate. Not for production use. */
export function __resetVocabCacheForTests(): void {
  VOCAB = vocabJson as VocabEntry[];
  initialised = false;
}

export function isVocabInitialised(): boolean {
  return initialised;
}
